import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useAppSelector} from "../../../../app/store";
import {currentCompanyIdSelector} from "../../../auth/reducer";
import Box from "../../../../common/components/Box";
import {useTranslation} from "react-i18next";
import {Field, Form, Formik} from "formik";
import Loader from '../../../../common/components/Loader';
import Table from "react-bootstrap/Table";
import TimePicker from "../../../../common/components/DateTimePicker/TimePicker";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import {AcceptModes, CompanyResponse, CompanyWorkday, CompanyWorkdayForm} from "../../models";
import {requestChangeCompanyWorkdays, requestGetCompany, requestSetPosSettings, requestSetRequireThermalBag} from "../../api";
import {getAxiosErrorMessage} from "../../../../common/utils/functions";
import {addNotify} from "../../../../common/actions/notify";
import {useDispatch} from "react-redux";
import ErrorPlug from "../../../../common/components/ErrorPlug";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {requiredMessage} from "../../../../common/utils/validation";
import Spinner from "react-bootstrap/Spinner";
import Icon from "../../../../common/components/Icon";
import Checkbox from "../../../../common/components/Checkbox";
import Radio from "../../../../common/components/Radio";

/**
 * Компонент просмотра графика работы и контактной информации текущего заведения
 */
const ProfileView: React.VFC = () => {
  
  const dispatch = useDispatch();
  const {t} = useTranslation();
  
  const companyId = useAppSelector(currentCompanyIdSelector);
  
  const [data, setData] = useState<CompanyResponse>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  
  /** статус монтирования компонента для присвоения результатов асинхронной операции */
  const isMounted = useRef(false);
  
  const getCompany = useCallback(() => {
    if (companyId) {
      if (isMounted.current) {
        setLoading(true);
      }
      
      requestGetCompany(companyId)
        .then(({data}) => {
          if (isMounted.current) {
            setData(data);
            setSelectedAcceptMode(data.acceptMode ?? AcceptModes.AutoPOS);
          }
        })
        .catch((err) => {
          if (isMounted.current) {
            setError(getAxiosErrorMessage(err));
          }
        })
        .finally(() => {
          if (isMounted.current) {
            setLoading(false);
          }
        })
    }
  }, [companyId]);
  
  useEffect(() => {
    isMounted.current = true;
    
    getCompany();
    
    return () => {
      isMounted.current = false;
    }
  }, [getCompany]);
  
  const [changeWorkDaysLoading, setChangeWorkDaysLoading] = useState(false);
  const [resetForm, setResetForm] = useState<CompanyWorkdayForm[] | null>(null);
  const [showThermalBagConfirm, setShowThermalBagConfirm] = useState(false);
  const [thermalBagLoading, setThermalBagLoading] = useState(false);
  const [acceptModeLoading, setAcceptModeLoading] = useState(false);
  const [selectedAcceptMode, setSelectedAcceptMode] = useState<AcceptModes>(AcceptModes.AutoPOS);
  const [acceptModeSaved, setAcceptModeSaved] = useState(false);
  
  /**
   * Изменить график работы
   * @param form - форма нового графика
   * @param isReset - если true, то изменение является сбросом и нужно убрать возможность сброса после успеха запроса
   */
  const changeWorkDays = (
    form: CompanyWorkdayForm[],
    isReset = false,
  ) => {
    if (companyId) {
      setChangeWorkDaysLoading(true);
      const resetFormToBe = isReset || !data?.workdays
        ? null
        : data.workdays;
      
      requestChangeCompanyWorkdays(companyId, form)
        .then(({data}) => {
          if (isMounted.current) {
            setData(data);
            setResetForm(resetFormToBe);
          }
        })
        .catch((err) => {
          dispatch(addNotify(getAxiosErrorMessage(err)));
        })
        .finally(() => {
          if (isMounted.current) {
            setChangeWorkDaysLoading(false);
          }
        })
    }
  }
  
  const handleThermalBagChange = () => {
    if (!data?.requireThermalBag) {
      setShowThermalBagConfirm(true);
    } else {
      toggleThermalBag(false);
    }
  };

  const handleAcceptModeChange = (mode: AcceptModes) => {
    setSelectedAcceptMode(mode);
    setAcceptModeSaved(false);
  };

  const saveAcceptMode = () => {
    if (companyId) {
      setAcceptModeLoading(true);
      requestSetPosSettings(companyId, {acceptMode: selectedAcceptMode})
        .then(() => {
          if (isMounted.current) {
            setData(prev => prev ? {...prev, acceptMode: selectedAcceptMode} : prev);
            setAcceptModeSaved(true);
          }
        })
        .catch((err) => {
          dispatch(addNotify(getAxiosErrorMessage(err)));
        })
        .finally(() => {
          if (isMounted.current) {
            setAcceptModeLoading(false);
          }
        });
    }
  };

  const toggleThermalBag = (value: boolean) => {
    if (companyId) {
      setThermalBagLoading(true);
      requestSetRequireThermalBag(companyId, {requireThermalBag: value})
        .then(({data}) => {
          if (isMounted.current) {
            setData(data);
          }
        })
        .catch((err) => {
          dispatch(addNotify(getAxiosErrorMessage(err)));
        })
        .finally(() => {
          if (isMounted.current) {
            setThermalBagLoading(false);
            setShowThermalBagConfirm(false);
          }
        });
    }
  };

  type WorkdayValues = {workDays: CompanyWorkday[]};
  
  if (error) {
    return <ErrorPlug onClick={getCompany}>
      {error}
    </ErrorPlug>
  }
  
  if (loading) {
    return <Loader/>
  }
  
  if (!data) {
    return null;
  }
  
  const {
    name,
    city,
    workdays,
  } = data;
  
  const initialValues: WorkdayValues = {
    workDays: [1, 2, 3, 4, 5, 6, 7].map(day =>
      workdays?.find(({day: _day}) => _day === day)
      ?? ({
        day,
        timeFrom: '',
        timeTo: '',
      })),
  }
  
  return <Box className="m-2 m-md-0">
    <h3 className="mb-0">{name}</h3>
    <p className="mb-4">{city?.name}</p>
    <Row>
      <Col md={7} lg={5}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={(values: WorkdayValues) => {
            changeWorkDays(values.workDays.filter(({timeFrom, timeTo}) => !!timeFrom && !!timeTo));
          }}>
          {({values, setFieldValue, setFieldTouched, isValid}) => {
  
            /**
             * Функция изменения вынесена сюда для исправления запаздывания
             */
            const onChange = (name: string, value: string | null) => {
              setFieldValue(name, value);
  
              /**
               * Исправление запаздывания валидации на один шаг
               */
              setTimeout(() => {
                if (isMounted.current) {
                  setFieldTouched(name)
                }
              }, 0)
            }
            
            return <Form>
              <h6 className="mb-2">{t('features.profile.schedule')}</h6>
              <Table striped borderless>
                <tbody>
                {values.workDays.map(({day, timeFrom, timeTo}, index) => {
  
                  const validate = () => !!timeTo !== !!timeFrom
                    ? requiredMessage()
                    : '';
                  
                  return <tr className="align-middle" key={day}>
                    <td className="fw-600 ps-2 ps-md-4 pe-2 pe-md-5 text-uppercase">
                      {t(`common.daysOfTheWeekShort.${translationKeys[day]}`)}
                    </td>
                    <td className="fw-600 ps-0 pe-0 pe-md-2 text-lowercase">
                      {t('common.from')}
                    </td>
                    <td>
                      <Field
                        name={`workDays.${index}.timeFrom`}
                        component={TimePicker}
                        className={'form-control'}
                        onChange={onChange}
                        validate={validate}
                        hasError={!!timeTo !== !!timeFrom}
                      />
                    </td>
                    <td className="fw-600 text-lowercase pe-0 pe-md-2">
                      {t('common.to')}
                    </td>
                    <td>
                      <Field
                        name={`workDays.${index}.timeTo`}
                        component={TimePicker}
                        className={'form-control'}
                        onChange={onChange}
                        validate={validate}
                        hasError={!!timeTo !== !!timeFrom}
                      />
                    </td>
                  </tr>
                })}
                </tbody>
              </Table>
              <div className="d-flex align-items-center">
                {!!resetForm && <Button
                  className="me-2_5"
                  disabled={changeWorkDaysLoading}
                  onClick={() => changeWorkDays(resetForm, true)}
                  variant="outline-secondary">
                  {t('common.actionCancel')}
                </Button>}
                <Button
                  disabled={!isValid || changeWorkDaysLoading}
                  type="submit"
                  variant="outline-success">
                  {t('common.actionSave')}
                </Button>
                {!changeWorkDaysLoading
                  && !!resetForm
                  && <Icon className="ms-2_5 green" icon={'check'}/>}
                {/* не через <Loader/>, так как там паддинг p-2
                    (из-за чего высота лоадера 48 > высоты кнопки в 39 и верстка смещается)
                    и паддинг сложно переопределить */}
                {changeWorkDaysLoading && <div className="d-flex justify-content-center ms-3">
                    <Spinner animation="border" variant="primary"/>
                </div>}
              </div>
            </Form>;
          }}
        </Formik>
      </Col>
      <Col md={5} lg={7} className="mt-4 mt-md-0 ps-md-5">
        <h5 className="mb-3">{t('features.profile.posIntegration')}</h5>
        <div>
          <h6 className="mb-2">{t('features.profile.acceptMode')}</h6>
          <Radio
              id="accept-mode-auto"
              name="acceptMode"
              value={AcceptModes.AutoPOS}
              checked={selectedAcceptMode === AcceptModes.AutoPOS}
              onChange={() => handleAcceptModeChange(AcceptModes.AutoPOS)}
              disabled={acceptModeLoading}
              label={t('features.profile.acceptModeAutoPOS')}
              className="mb-2"
          />
          <Radio
              id="accept-mode-manual"
              name="acceptMode"
              value={AcceptModes.Manual}
              checked={selectedAcceptMode === AcceptModes.Manual}
              onChange={() => handleAcceptModeChange(AcceptModes.Manual)}
              disabled={acceptModeLoading}
              label={t('features.profile.acceptModeManual')}
          />
          <div className="d-flex align-items-center mt-3">
            <Button
                disabled={acceptModeLoading}
                onClick={saveAcceptMode}
                variant="outline-success">
              {t('common.actionSave')}
            </Button>
            {!acceptModeLoading && acceptModeSaved && <Icon className="ms-2_5 green" icon={'check'}/>}
            {acceptModeLoading && <div className="d-flex justify-content-center ms-3">
              <Spinner animation="border" variant="primary"/>
            </div>}
          </div>
        </div>
        {data.hasEmenuDelivery && !data.hasEmenuDelivery && (
            <div className="mt-4">
              <Checkbox
                  id="require-thermal-bag"
                  checked={!!data.requireThermalBag}
                  onChange={handleThermalBagChange}
                  disabled={thermalBagLoading}
                  label={t('features.profile.requireThermalBag')}
              />
            </div>
        )}
        <Modal show={showThermalBagConfirm} centered onHide={() => setShowThermalBagConfirm(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{t('features.profile.requireThermalBagConfirmTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('features.profile.requireThermalBagConfirmText')}
          </Modal.Body>
          <Modal.Footer>
            <Button
                variant="outline-secondary"
                onClick={() => setShowThermalBagConfirm(false)}
                disabled={thermalBagLoading}
            >
              {t('common.actionCancel')}
            </Button>
            <Button
                variant="outline-primary"
                onClick={() => toggleThermalBag(true)}
                disabled={thermalBagLoading}
            >
              {t('features.profile.requireThermalBagConfirmEnable')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Col>
    </Row>
  </Box>
};

const translationKeys: Record<number, string> = {
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
  7: 'sunday',
}

export default ProfileView;
